

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import FolderTableHeader from "@/components/Controls/FolderTableHeader.vue"
import DataTable from "../DataTable.vue"
import ReportEditor from "./ReportEditor.vue"

import FolderService from "@/services/folder-service"

import api from "@/services/api"
import reportApi from "@/services/api.reports"

import User, { CurrentUser } from "@/types/user"
import { Insight, InsightSummary } from "@/types/insights"
import { Report, ReportDetails, ScheduledReport } from "@/types/report"
import { FolderType, FolderItems } from "@/types/folder"

import StringValidation from "@/scripts/stringValidation"

/**
 * @summary Component for the top level reports listing. Allows for creating, editing and deleting.
 */
@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"folder-header": FolderTableHeader,
		"data-table": DataTable,
		"skeleton": SkeletonLoader,
		"report-editor": ReportEditor
	}
})
export default class ReportSetup extends Vue {
	// --- VueX ---

	@Getter("getMe")
	public me!: CurrentUser

	// --- Data ---

	private reports: Report[] = []
	private insights: InsightSummary[] = []

	private isLoadingOnMount: boolean = true
	private isLoadingMisc: boolean = false

	private toastMessage: string | null = null
	private editReport: ScheduledReport | null = null

	private reportScheduleValid = true

	private folderService = new FolderService()
	folderType = FolderType.Report

	// --- Computed Properties ---

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title),
				hasTemplate: true
			},
			{
				key: "valid",
				inTable: false,
				inModal: () => false,
				errors: (reportDetails: ScheduledReport) => {
					if (!reportDetails) return null
					if (reportDetails.sections.some(s => !s.title || s.title === "")) return "Some sections do not have a title"
					if (reportDetails.scheduled && !reportDetails.timeZone) return "Select a time zone for Scheduling"
					if (reportDetails.scheduled && !this.reportScheduleValid) return "Scheduling Invalid"

					return null
				}
			},
			{
				key: "download",
				label: "",
				inTable: true,
				hasTemplate: true,
				inModal: () => false
			}
		]
	}

	private get folders () {
		return this.folderService.getPermittedSelectionFolders()
	}

	private get folderReports () {
		return this.folderService.tableRows()
	}

	private get isLoading () {
		return this.isLoadingOnMount || this.isLoadingMisc
	}

	// --- Lifecycle Hooks ---

	async mounted () {
		await this.load(true)
		this.isLoadingOnMount = false
	}

	// --- Event Handlers ---

	createNewItem () {
		return this.editReport = {
			title: "",
			sections: [],
			folder: null
		}
	}

	/**
	* @summary Loads reports, folders and insights in parallel, then loads reports into folder service.
	* @param {boolean} loadInsights - if insights should be loaded, default false
	*/
	private async load (loadInsights: boolean = false) {
		const promisedContent: Promise<any>[] = [
			this.folderService.load(this.me, this.folderType),
			reportApi.reportGetList().then(reports => this.reports = reports)
		]

		if (loadInsights) {
			promisedContent.push(api.insightsGet().then(insights => this.insights = insights))
		}

		await Promise.all(promisedContent)
		this.folderService.loadFolderItemGroups(this.reports)
	}

	/**
	 * @summary Loads additional details for a specific report. This includes the record sections (and the cells on those sections)
	 * @returns Report with additional details
	 */
	private async loadReportDetails (editItem: Report): Promise<ScheduledReport> {
		return this.editReport = await reportApi.reportGet(editItem.id!)
	}

	/**
	 * @summary Saves a report into the data service, then updates report list with changes
	 * @param {ScheduledReport} saveItem - Report details to save.
	 */
	private async save (saveItem: ScheduledReport) {
		const createdReport = await reportApi.reportSave(saveItem)

		if (saveItem.id) {
			const existingReport = this.reports.find(r => r.id === saveItem.id)
			if (existingReport) {
				existingReport.title = createdReport.title
				existingReport.folder = createdReport.folder
			}
		} else {
			this.reports.push({
				id: createdReport.id,
				title: createdReport.title,
				folder: createdReport.folder
			})
		}

		this.folderService.loadFolderItemGroups(this.reports)
	}

	/**
	 * @summary Deletes a report then removes from reports list
	 * @param {ReportDetails} deleteItem - Report to delete
	 */
	private async deleteItem (deleteItem: ReportDetails) {
		await reportApi.reportDelete(deleteItem.id!)
		this.reports.removeWhere(r => r.id === deleteItem.id)
		this.folderService.loadFolderItemGroups(this.reports)
	}

	/**
	 * @summary Downloads the currently open report (if there is one).
	 * This report does not have to exist in the database
	 */
	private async downloadReport () {
		if (!this.editReport) return

		this.toastMessage = "Generating" + this.editReport.title || "Report"
		try {
			await reportApi.downloadReport(this.editReport)
		} finally {
			this.toastMessage = null
		}
	}

	/**
	 * @summary Sends out Test Emails to the Recipients.
	 * This report does not have to exist in the database
	 */
	private async sendTestEmail () {
		if (!this.editReport) return

		this.toastMessage = "Sending Test emails for: " + this.editReport.title || "Report"

		setTimeout(async () => {
			this.toastMessage = null
		}, 1500)
		await reportApi.triggerReportEmails(this.editReport)
	}

	/**
	 * @summary Downloads a specific report by Id. Saved values for this report will be used.
	 */
	private async downloadReportById (id: string, title: string | null) {
		this.toastMessage = "Generating" + title || "Report"
		try {
			await reportApi.downloadReportById(id, title || "Report")
		} finally {
			this.toastMessage = null
		}
	}

	/**
	 * @summary Toggles the expanded state of a folder
	 * @param {FolderItems} folder - The folder to toggle
	 */
	private toggleExpandFolder (folder: FolderItems) {
		if (!folder.isFolderType) {
			return
		}

		folder.expanded = !folder.expanded
	}
}

